import { Box, Stack, Typography } from '@mui/joy';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useRef, useLayoutEffect, useState } from 'react';
import { useRecipeGridColumns } from '@/features/recipes/hooks/useRecipeGridColumns';
import { VirtualRecipeRows } from './VirtualRecipeRows';
import { useRecipeRows } from '@/features/recipes/hooks/useRecipeRows';

export function RecipeGrid({ recipes, card }) {
  const listRef = useRef(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [rowHeight, setRowHeight] = useState(260); // until a real row is measured
  const { columns, gap, isMobile } = useRecipeGridColumns();

  const rows = useRecipeRows(recipes, columns);

  useLayoutEffect(() => {
    if (listRef.current) {
      setScrollMargin(listRef.current.offsetTop);
    }
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    overscan: 3,
    scrollMargin,
  });

  // Rows are measured only as they scroll into view, so a wrong estimate
  // resizes the page mid-scroll and breaks smooth scrolling.
  useLayoutEffect(() => {
    const row = listRef.current?.querySelector('[data-index]');
    const measured = Math.round(row?.getBoundingClientRect().height ?? 0);
    if (measured > 0 && measured !== rowHeight) {
      setRowHeight(measured);
      virtualizer.measure();
    }
  }, [columns, rows.length, rowHeight, virtualizer]);

  // No background/bleed of its own -- the parent (RecipesPage) already
  // extends the canvas background across the whole page, header included.
  if (recipes.length === 0) {
    return <NoRecipesFound />;
  }

  return (
    <Box ref={listRef} component="ul" sx={{ flex: 1, margin: 0, padding: 0, listStyle: 'none' }}>
      <VirtualRecipeRows
        virtualizer={virtualizer}
        rows={rows}
        columns={columns}
        gap={gap}
        card={card}
        isMobile={isMobile}
        scrollMargin={scrollMargin}
      />
    </Box>
  );
}

function NoRecipesFound() {
  return (
    <Stack gap={1} sx={{ p: 5, flex: 1 }}>
      <Typography level="h4" sx={{ textAlign: 'center' }}>No recipes found</Typography>
      <Typography level="body-sm" sx={{ textAlign: 'center' }}>Try adding a new recipe, changing your filter, or searching again.</Typography>
    </Stack>
  );
}
